import { endOfDay, isAfter, isBefore, isSameDay, startOfDay } from "date-fns";
import { useState } from "react";
import { useLiveRef } from "reakit-utils";

import { TimeReport } from "@/data/time-reports";
import { getDateRange, isDateBetweenDays, middleOfDay } from "@/utils/dates";

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

export const useTimeReportCalendarNavigation = (timeReport: TimeReport) => {
  const [state, setState] = useState(() => ({ dayIndex: 0, weekIndex: 0 }));

  const startDate = new Date(timeReport.startDate);
  const endDate = new Date(timeReport.endDate);

  const { days, weeks } = getDateRange(startDate, endDate);

  const stateRef = useLiveRef(state);

  const today = middleOfDay(new Date());

  const firstTimeReportDay = days[0];
  const lastTimeReportDay = days[days.length - 1];

  const clampDayIndex = (n: number) => clamp(n, 0, days.length - 1);
  const clampWeekIndex = (n: number) => clamp(n, 0, weeks.length - 1);

  const goToDate = (newDate: Date) => {
    setState(currentState => {
      let newDayIndex = currentState.dayIndex;
      let newWeekIndex = currentState.weekIndex;

      if (
        isDateBetweenDays({
          date: newDate,
          startDate: firstTimeReportDay,
          endDate: lastTimeReportDay,
        })
      ) {
        newDayIndex = clampDayIndex(days.findIndex(d => isSameDay(d, newDate)));
        newWeekIndex = clampWeekIndex(
          weeks.findIndex(w => isDateBetweenDays({ date: newDate, startDate: w[0], endDate: w[6] })),
        );
      } else if (isSameDay(newDate, firstTimeReportDay) || isBefore(newDate, startOfDay(firstTimeReportDay))) {
        newDayIndex = 0;
        newWeekIndex = 0;
      } else if (isSameDay(newDate, lastTimeReportDay) || isAfter(newDate, endOfDay(lastTimeReportDay))) {
        newDayIndex = days.length - 1;
        newWeekIndex = weeks.length - 1;
      }

      return {
        dayIndex: newDayIndex,
        weekIndex: newWeekIndex,
      };
    });
  };

  return {
    days,
    weeks,

    currentDay: days[state.dayIndex],
    currentWeek: weeks[state.weekIndex],

    canGoToToday: isDateBetweenDays({
      date: today,
      startDate: firstTimeReportDay,
      endDate: lastTimeReportDay,
    }),
    canGoToPrevWeek: weeks.length > 1 && state.weekIndex > 0,
    canGoToNextWeek: weeks.length > 1 && state.weekIndex < weeks.length - 1,

    goToDate,
    goToToday: () => goToDate(today),
    goToPrevWeek: () => goToDate(weeks[clampWeekIndex(stateRef.current.weekIndex - 1)][0]),
    goToNextWeek: () => goToDate(weeks[clampWeekIndex(stateRef.current.weekIndex + 1)][0]),
  };
};
